import type { AwardCardView, MatchupSummary, StatsFreshnessShape } from '@fsp/domain';
import { useLeagueBase } from '../../data/hooks';
import { Detail } from '../../data/detail';
import { LiveDot, ScoreNumber, TeamMark } from '../../kit';
import { Colored, Cover, SplitBar } from '../../kit/ui';
import { TeamName } from './Names';
import { AwardIcon } from './AwardKit';
import { num } from './notComputed';
import { Term } from '../../kit/Why';

export function MatchupCard({
  m,
  compact = false,
  link = true,
  leagueId,
  awards,
  stats,
  leftTeamId,
}: {
  m: MatchupSummary;
  /** The team that reads on the left, when it is not mine: another team's page (B-220). */
  leftTeamId?: string;
  compact?: boolean;
  link?: boolean;
  /** The league this matchup belongs to; defaults to the active league. The hub passes it. */
  leagueId?: string;
  /** The period's awards (AWARDS.md): a final card wears a chip for each side that won one. */
  awards?: AwardCardView[];
  /**
   * How fresh the period's lines are, when the screen has it (BACKLOG L2). A
   * matchup in progress is "Live" only while the numbers are; otherwise it is
   * "In progress", and the screen says when they were last updated.
   */
  stats?: StatsFreshnessShape;
}) {
  const activeBase = useLeagueBase();
  const base = leagueId ? `/l/${leagueId}` : activeBase;
  // Your team always reads on the left — or the team whose page this is.
  const flip = leftTeamId !== undefined ? m.away.team.id === leftTeamId : m.away.team.isMine;
  const left = flip ? 'away' : 'home';
  const right = flip ? 'home' : 'away';
  const homeWin = m.winProbabilityHome;
  // Null stays null rather than collapsing to a half: an even-looking bar is
  // indistinguishable from an evenly matched game (E1, 2026-09-19).
  const leftWin = homeWin === null ? null : flip ? 1 - homeWin : homeWin;
  // This matchup's own week: a list handed in for another week — Home's is
  // the last settled one — wears nothing here.
  const chipsFor = (teamId: string) =>
    (awards ?? []).filter(
      (a) => !a.asItStands && a.period === m.period && a.winners.some((w) => w.team.id === teamId),
    );
  // One chip a side on a compact card, two on the full one; the rest live on the Awards screen.
  const perSide = compact ? 1 : 2;
  const leftChips = m.status === 'final' ? chipsFor(m[left].team.id).slice(0, perSide) : [];
  const rightChips = m.status === 'final' ? chipsFor(m[right].team.id).slice(0, perSide) : [];
  // THE CARD OPENS ITS MATCHUP, AND EACH TEAM'S NAME OPENS THAT TEAM
  // (B-220): the card's door is laid under it (`Cover`) and the names sit
  // above it, rather than the whole card being one link with the names
  // inside it as words.
  return (
    <div className={`fsp-surface fsp-mcard${link ? ' fsp-cover-host fsp-mcard-link' : ''}`}>
      {link ? <Cover to={`${base}/matchup/${m.id}`} label={coverLabel(m)} /> : null}
      <div className="fsp-mcard-sides">
        <Side m={m} side={left} compact={compact} base={link ? base : null} />
        <Side m={m} side={right} compact={compact} right base={link ? base : null} />
      </div>
      <div className="fsp-mcard-foot">
        {leftWin === null ? null : (
          <SplitBar
            share={leftWin}
            left={m[left].team.colors.primary}
            right={m[right].team.colors.primary}
          />
        )}
        <div className="fsp-mcard-status">
          <span className="fsp-mcard-marks">
            {m.status === 'live' && stats !== undefined && stats.state !== 'live' ? (
              // A week under way whose numbers arrive overnight is not live:
              // a pulsing dot over a score that has not moved since this
              // morning says it is (round 3, N5).
              <span className="fsp-mcard-strong">In progress</span>
            ) : m.status === 'live' ? (
              <LiveDot label={m.liveLabel ? `Live · ${m.liveLabel}` : 'Live'} />
            ) : m.status === 'final' ? (
              <span className="fsp-mcard-final">
                FINAL
                {leftChips.map((a) => (
                  // The award's line when asked (B-374), as the matchup chip's.
                  <Term key={a.id} tip={a.line} name={a.name} className="fsp-matchup-term">
                    <span className="fsp-award-chip" data-tone={a.tone}>
                      <AwardIcon icon={a.icon} size={11} />
                      {a.name}
                    </span>
                  </Term>
                ))}
              </span>
            ) : m.home.projected === null && m.away.projected === null ? (
              // Nobody projected it — the schedule's cards, a hub card — so
              // "Projected" over two zeros would name nothing (B-176).
              <span className="fsp-muted">Upcoming</span>
            ) : (
              <span className="fsp-muted">Projected</span>
            )}
            {/* The left side's chance — mine, when I play — on the left, under
                that side's share of the bar. At the right end it read as the
                other side's chance (B-170). A final needs none: the score says it.
                At Everything, as on the matchup and My Team: the percentage is
                win probability, a Pro number (DETAIL_LEVELS §Scoreboard), and
                this card printed it at Simple (SCREENS-6). */}
            {/* No chance nobody worked out: "— to win" said nothing (plan
                rule 8; the page-by-page review of 2026-10-11). */}
            {m.status === 'final' || leftWin === null ? null : (
              <Detail min="pro">
                <span className="fsp-muted">
                  ·{' '}
                  <span className="fsp-num fsp-mcard-chance">{`${Math.round(leftWin * 100)}%`}</span>{' '}
                  to win
                </span>
              </Detail>
            )}
          </span>
          <span className="fsp-muted fsp-mcard-marks">
            {rightChips.map((a) => (
              <Term key={a.id} tip={a.line} name={a.name} className="fsp-matchup-term">
                <span className="fsp-award-chip" data-tone={a.tone}>
                  <AwardIcon icon={a.icon} size={11} />
                  {a.name}
                </span>
              </Term>
            ))}
          </span>
        </div>
        {m.rivalry ? (
          <div className="fsp-sub fsp-rivalry">
            <Term tip="The all-time series between these two teams, playoffs included.">
              {m.rivalry.line}
            </Term>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** What the card's door says to a screen reader: whose game, and where it stands. */
function coverLabel(m: MatchupSummary): string {
  const where = m.status === 'final' ? 'final' : m.status === 'live' ? 'in progress' : 'upcoming';
  return `${m.home.team.name} against ${m.away.team.name}, ${where}: open the matchup`;
}

function Side({
  m,
  side,
  compact,
  right,
  base,
}: {
  m: MatchupSummary;
  side: 'home' | 'away';
  compact: boolean;
  right?: boolean;
  /** The league's prefix when the name links to the team (B-220); null for words. */
  base: string | null;
}) {
  const s = m[side];
  const other = m[side === 'home' ? 'away' : 'home'];
  // Ahead, or won: a final is marked by who won it, which on a tie the league
  // broke is not the higher score (N1, B-232).
  const winning = m.status === 'final' ? m.winnerTeamId === s.team.id : s.score > other.score;
  // The side wears its team's color, as data: the wash's strength is the
  // stylesheet's, stronger on my own side (`.fsp-mcard-side[data-mine]`).
  return (
    <Colored
      className={`fsp-mcard-side ${compact ? 'compact' : ''} ${right ? 'right' : ''}`}
      colors={{ '--team': s.team.colors.primary }}
      data-tone={s.team.isMine ? 'mine' : undefined}
      // Which side is ahead, for a look to mark (Crayon underlines the score).
      data-lead={winning ? 'true' : undefined}
    >
      <div className="fsp-mcard-head">
        {!right ? <TeamMark team={s.team} size={compact ? 22 : 28} /> : null}
        <div className="fsp-mcard-who">
          <div className="fsp-mcard-name">
            {base === null ? s.team.name : <TeamName team={s.team} base={base} />}
          </div>
          <div className="fsp-sub">
            {s.team.record.wins}–{s.team.record.losses}
            {s.team.record.ties ? `–${s.team.record.ties}` : ''} · {s.team.ownerName}
          </div>
        </div>
        {right ? <TeamMark team={s.team} size={compact ? 22 : 28} /> : null}
      </div>
      <div className="fsp-mcard-score">
        <ScoreNumber
          value={s.score}
          size={compact ? 30 : 40}
          color={winning ? 'var(--fsp-ink-primary)' : 'var(--fsp-ink-secondary)'}
        />
      </div>
      {!compact ? (
        <div className="fsp-sub fsp-mcard-proj">
          {s.yetToPlay} yet to play
          {/* No projected final where none is served: a league that keeps no
              projections (RULES_SCHEMA §15c). */}
          {s.projected !== null ? (
            <>
              {' '}
              · proj <span className="fsp-num">{num(s.projected)}</span>
            </>
          ) : null}
        </div>
      ) : m.status !== 'final' && (s.projected !== null || other.projected !== null) ? (
        // THE COMPACT CARD'S PROJECTED FINAL, at Standard and above, which
        // is what DETAIL_LEVELS gives the scoreboard there. The scoreboard
        // serves both totals since N3 and drew neither: a card said
        // "Projected" under a bar and two zeros (round 3, N6). Not on a final,
        // where it is the score; not where nobody has worked one out (the
        // schedule's cards, whose totals the league reads do not serve yet).
        <Detail min="standard">
          <div className="fsp-sub fsp-mcard-proj-compact">
            {s.yetToPlay} to play · proj <span className="fsp-num">{num(s.projected)}</span>
          </div>
        </Detail>
      ) : null}
    </Colored>
  );
}
